import React, { useEffect } from 'react';

import { Section, Grid } from '@gogame/components';
import { SidePageV2, withForm, FormField, getIn } from '@gogame/form';

import {
  APPLICATIONS,
  AFFILIATES,
  NETWORKS,
  CLUSTERS,
  PLATFORMS,
  TYPE,
  SUB_TYPE,
  CHANNELS,
  PERIOD,
  DESCRIPTION,
  FORM_FIELDS_DEPENDENCIES,
  TYPE_MARKETING,
} from 'scenes/Annotations/constants';
import {
  loadApplications,
  loadAffiliates,
  loadTypes,
  loadSubTypes,
  loadChannels,
  loadNetworks,
  loadClusters,
  loadPlatforms,
} from 'scenes/Annotations/sources/fields';

import {
  annotationSchema,
  subTypesParamsSchema,
  clustersParamsSchema,
} from './schemas';

const AnnotationForm = withForm({
  validationSchema: annotationSchema,
})(
  ({
    values,
    setFieldValue,
    onClose,
    title,
    subTitle,
    loading,
    hierarchyState,
    onExited,
    open,
    entityId,
  }) => {
    const handleSubTypeValidation = () => {
      const typeValue = getIn(values, TYPE);
      const subTypeValue = getIn(values, SUB_TYPE);
      return [0, 1].includes(typeValue) && subTypeValue === undefined
        ? 'Sub type is required'
        : undefined;
    };

    useEffect(() => {
      if (values[TYPE] !== TYPE_MARKETING && values?.[CHANNELS]?.length) {
        setFieldValue(CHANNELS, undefined);
      }
    }, [values[TYPE], values?.[CHANNELS]]);

    const subTypesDisabled = !getIn(hierarchyState, `${SUB_TYPE}.data.length`);
    return (
      <SidePageV2
        onClose={onClose}
        onExited={onExited}
        open={open}
        loading={loading}
        size={'lg'}
      >
        <SidePageV2.Title
          title={title}
          subTitle={subTitle}
          entityId={entityId}
        />
        <SidePageV2.NewBody>
          <Section title={'Period'}>
            <Grid container spacing={2}>
              <Grid item xs={4}>
                <FormField.DateRangePicker
                  label={'Period'}
                  name={PERIOD}
                  placeholder={'Select period'}
                  presets={[]}
                  padding={0}
                  required={true}
                />
              </Grid>
            </Grid>
          </Section>
          <Section title={'Type'}>
            <Grid container spacing={2}>
              <Grid item xs={4}>
                <FormField.Select
                  label={'Type'}
                  name={TYPE}
                  source={loadTypes}
                  placeholder={'Select type'}
                  padding={0}
                  required={true}
                />
              </Grid>
              <Grid item xs={4}>
                <FormField.Select
                  label={'Sub type'}
                  name={SUB_TYPE}
                  source={loadSubTypes} // TODO: don't open drop with empty options
                  depends={FORM_FIELDS_DEPENDENCIES[SUB_TYPE]}
                  disabled={subTypesDisabled}
                  active={subTypesParamsSchema.isValidSync(values)}
                  placeholder={'Select sub type'}
                  validate={handleSubTypeValidation}
                  padding={0}
                  required={true}
                />
              </Grid>
              <Grid item xs={4}>
                <FormField.MultiSelect
                  label={'Channels'}
                  name={CHANNELS}
                  source={loadChannels}
                  depends={FORM_FIELDS_DEPENDENCIES[CHANNELS]}
                  disabled={values[TYPE] !== TYPE_MARKETING}
                  placeholder={'All'}
                  padding={0}
                />
              </Grid>
            </Grid>
          </Section>
          <Section title={'Targeting'}>
            <Grid container spacing={2}>
              <Grid item container spacing={2}>
                <Grid item xs={4}>
                  <FormField.MultiSelect
                    label={'Application'}
                    name={APPLICATIONS}
                    source={loadApplications}
                    depends={FORM_FIELDS_DEPENDENCIES[APPLICATIONS]}
                    placeholder={'All'}
                    padding={0}
                  />
                </Grid>

                <Grid item xs={4}>
                  <FormField.MultiSelect
                    label={'Networks'}
                    name={NETWORKS}
                    source={loadNetworks}
                    depends={FORM_FIELDS_DEPENDENCIES[NETWORKS]}
                    placeholder={'All'}
                    padding={0}
                  />
                </Grid>
                <Grid item xs={4}>
                  <FormField.MultiSelect
                    label={'Clusters'}
                    name={CLUSTERS}
                    source={loadClusters}
                    depends={FORM_FIELDS_DEPENDENCIES[CLUSTERS]}
                    active={clustersParamsSchema.isValidSync(values)}
                    placeholder={'All'}
                    padding={0}
                  />
                </Grid>
              </Grid>
              <Grid item container spacing={2}>
                <Grid item xs={4}>
                  <FormField.MultiSelect
                    label={'Platforms'}
                    name={PLATFORMS}
                    source={loadPlatforms}
                    depends={FORM_FIELDS_DEPENDENCIES[PLATFORMS]}
                    placeholder={'All'}
                    padding={0}
                  />
                </Grid>
                <Grid item xs={8}>
                  <FormField.MultiSelect
                    label={'Affiliates'}
                    name={AFFILIATES}
                    source={loadAffiliates}
                    depends={FORM_FIELDS_DEPENDENCIES[AFFILIATES]}
                    placeholder={'All'}
                    padding={0}
                  />
                </Grid>
              </Grid>
            </Grid>
          </Section>
          <Section title={'Description'}>
            <FormField.TextField
              multiline={true}
              label={'Description*'}
              name={DESCRIPTION}
              padding={0}
            />
          </Section>
        </SidePageV2.NewBody>
        <SidePageV2.Actions>
          <SidePageV2.CloseButton />
          <SidePageV2.SubmitButton />
        </SidePageV2.Actions>
      </SidePageV2>
    );
  }
);

export default AnnotationForm;
